/**
 * Labstep
 *
 * @module prosemirror/nodes
 * @desc ProseMirror Nodes Definitions
 */

import { NodeBlockquote } from 'labstep-web/prosemirror/nodes/blockquote';
import { NodeStep as NodeCode } from 'labstep-web/prosemirror/nodes/code';
import { NodeCodeBlock } from 'labstep-web/prosemirror/nodes/code-block';
import { NodeConditions } from 'labstep-web/prosemirror/nodes/conditions';
import { NodeDate } from 'labstep-web/prosemirror/nodes/date';
import { NodeDoc } from 'labstep-web/prosemirror/nodes/doc';
import { NodeExperiment } from 'labstep-web/prosemirror/nodes/experiment';
import { NodeStep as NodeExperimentStep } from 'labstep-web/prosemirror/nodes/experiment-step';
import { NodeExperimentTable } from 'labstep-web/prosemirror/nodes/experiment-table';
import { NodeTimer as NodeExperimentTimer } from 'labstep-web/prosemirror/nodes/experiment-timer';
import { NodeValue as NodeExperimentValue } from 'labstep-web/prosemirror/nodes/experiment-value';
import { NodeExperimentWorkflowLink } from 'labstep-web/prosemirror/nodes/experiment-workflow-link';
import { NodeFile } from 'labstep-web/prosemirror/nodes/file';
import { NodeHardBreak } from 'labstep-web/prosemirror/nodes/hard-break';
import { NodeHeading } from 'labstep-web/prosemirror/nodes/heading';
import { NodeHorizontalRule } from 'labstep-web/prosemirror/nodes/horizontal-rule';
import { NodeImage } from 'labstep-web/prosemirror/nodes/image';
import { NodeJupyterNotebook } from 'labstep-web/prosemirror/nodes/jupyter-notebook';
import { NodeMetadata } from 'labstep-web/prosemirror/nodes/metadata';
import { NodeMolecule } from 'labstep-web/prosemirror/nodes/molecule';
import { NodeParagraph } from 'labstep-web/prosemirror/nodes/paragraph';
import { NodeStep as NodeProtocolStep } from 'labstep-web/prosemirror/nodes/protocol-step';
import { NodeProtocolTable } from 'labstep-web/prosemirror/nodes/protocol-table';
import { NodeTimer as NodeProtocolTimer } from 'labstep-web/prosemirror/nodes/protocol-timer';
import { NodeValue as NodeProtocolValue } from 'labstep-web/prosemirror/nodes/protocol-value';
import { NodeReference } from 'labstep-web/prosemirror/nodes/reference';
import { NodeSmartTable } from 'labstep-web/prosemirror/nodes/smart-table';
import { keymap } from 'prosemirror-keymap';
import type { NodeSpec } from 'prosemirror-model';
import type { Plugin } from 'prosemirror-state';
import {
  NodeBulletList,
  NodeListItem,
  NodeOrderedList,
} from './list';
import {
  NodeTable,
  NodeTableCell,
  NodeTableHeader,
  NodeTableRow,
} from './table';
import NodeText from './text';

export const nodes = [
  NodeParagraph,
  NodeDoc,
  NodeText,
  NodeHorizontalRule,
  NodeCodeBlock,
  NodeBlockquote,
  NodeImage,
  NodeHeading,
  NodeHardBreak,
  NodeTable,
  NodeTableCell,
  NodeTableHeader,
  NodeTableRow,
  NodeCode,
  NodeFile,
  NodeMetadata,
  NodeConditions,
  NodeDate,
  NodeSmartTable,
  NodeExperiment,
  NodeExperimentTimer,
  NodeExperimentValue,
  NodeExperimentWorkflowLink,
  NodeExperimentTable,
  NodeExperimentStep,
  NodeJupyterNotebook,
  NodeMolecule,
  NodeProtocolTimer,
  NodeProtocolValue,
  NodeProtocolTable,
  NodeProtocolStep,
  NodeBulletList,
  NodeListItem,
  NodeOrderedList,
  NodeReference,
];

export const spec: Record<string, NodeSpec> = nodes.reduce(
  (result, node) => ({ ...result, [node.key]: node.spec }),
  {},
);

const nodesKeymap: Plugin[] = nodes.reduce(
  (result, node) =>
    node.keymap ? [...result, keymap(node.keymap)] : result,
  [] as Plugin[],
);

const nodesPlugins = nodes
  .filter((node) => node.plugins)
  .reduce(
    (result, node) =>
      node.plugins ? [...result, ...node.plugins] : result,
    [] as Plugin[],
  );

export const plugins = [...nodesKeymap, ...nodesPlugins];
